import useGroupsContext from '@contexts/GroupsContext/hook';
import { useComunidadesQueOPainelServe } from '@hooks/useComunidadesQueOPainelServe';

/**
 * Se o painel — e os cinco detalhes que descem dele — tem o que servir a esta pessoa.
 *
 * **O critério mudou com a #360, e o nome do arquivo ficou.** Era "tem papel de comissão em alguma
 * comunidade", porque o painel classificava a seleção inteira por um papel único e não sabia servir
 * quem não tinha nenhum. Agora ele monta uma família por escopo resolvido, e a família de produtor
 * serve exatamente a comunidade alcançada por grant com escopo de produto — que não tem papel de
 * comissão nenhum.
 *
 * Então a pergunta certa passou a ser: **alguma família serve alguma comunidade minha?** É a mesma
 * regra que decide os blocos, aplicada a tudo o que a pessoa alcança em vez de à seleção.
 *
 * Sem descritor — `platform-api` anterior à api#277, na janela de deploy — cai no critério antigo. É
 * o que mantém o comportamento de hoje enquanto o campo não chega.
 *
 * Existe como hook porque são SEIS telas: o painel e os cinco detalhes que compartilham o
 * `ConversionFilters`. Seis cópias divergem, e divergir aqui deixa uma tela abrindo vazia enquanto
 * as outras explicam.
 *
 * **`admiteSemPapel` é o mesmo parâmetro que o `ConversionFilters` recebe, e tem de ser o mesmo
 * valor.** Portão e filtro respondem à mesma pergunta — "esta tela sabe servir quem chega sem papel
 * de comissão?" —, e discordar entre eles produz o pior dos dois mundos: portão aberto e filtro
 * pedindo um papel que a pessoa não tem, ou seja, a tela em branco que o portão existe para evitar.
 */
export const useTemPapelDeComissao = (admiteSemPapel = false) => {
  const { groups, escopoPorProduto } = useGroupsContext();
  const servidas = useComunidadesQueOPainelServe();

  const temPapelDeComissao = Boolean(
    groups.owner.length || groups.coproducer.length || groups.affiliated.length,
  );

  /*
    O critério novo vale só onde a tela SABE servir a ausência de papel, e é a mesma condição que o
    `ConversionFilters` recebe.

    Sem isso o portão abria para o colaborador nos cinco detalhes, que continuam pedindo papel: o
    filtro caía no papel de partida — "Criador", que ele não tem —, a lista abria vazia e a tela ficava
    em branco. Portão aberto sem filtro que o acompanhe é a tela vazia que o portão existe para
    evitar.

    Descritor ausente cai no critério antigo pela mesma razão, e é o caso do deploy em curso.
  */
  if (!admiteSemPapel || !escopoPorProduto.length) return temPapelDeComissao;

  /*
    A MESMA lista que o seletor usa, pela mesma função: portão e lista respondendo "quais comunidades
    servem" com critérios diferentes é o que produz tela aberta e vazia.
  */
  return servidas.length > 0;
};
